import React from 'react';
import { themed } from '../../ui/theme';

export type WorkspaceMetricTone = 'critical' | 'warning' | 'progress' | 'success' | 'neutral';

export type WorkspaceMetric = {
  label: string;
  value: React.ReactNode;
  tone?: WorkspaceMetricTone;
  detail?: string;
};

type WorkspaceOverviewProps = {
  profile: string;
  description?: string;
  metrics?: WorkspaceMetric[];
  updatedLabel?: string;
  loading?: boolean;
};

const toneClasses: Record<WorkspaceMetricTone, string> = {
  critical: 'bg-red-500',
  warning: 'bg-amber-400',
  progress: 'bg-sky-500',
  success: 'bg-emerald-400',
  neutral: 'bg-slate-400',
};

const metricGridClasses: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-3',
  4: 'grid-cols-2 md:grid-cols-4',
  5: 'grid-cols-2 md:grid-cols-5',
};

const WorkspaceOverview: React.FC<WorkspaceOverviewProps> = ({
  profile,
  description = 'Fila única, ordenada por prazo e impacto.',
  metrics = [],
  updatedLabel = 'Atualizado agora',
  loading = false,
}) => {
  const surfaceClass = themed({
    minimal: 'rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950',
    saas: 'rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-950',
  });
  const gridClass = metricGridClasses[Math.min(metrics.length, 5)] ?? 'grid-cols-2 md:grid-cols-4';

  return (
    <section aria-labelledby="workspace-title" className="space-y-2">
      <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-1">
        <div>
          <h1 id="workspace-title" className="text-lg font-semibold tracking-tight text-slate-900 dark:text-white">
            Área de trabalho: {profile}
          </h1>
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{description}</p>
        </div>
        <span className="text-[11px] text-slate-500 dark:text-slate-400">{updatedLabel}</span>
      </div>

      {metrics.length > 0 && (
        <div className={`${surfaceClass} grid ${gridClass} overflow-hidden`} aria-label="Resumo da área de trabalho">
          {metrics.map((metric, index) => (
            <div
              key={metric.label}
              className={`min-w-0 px-3 py-2 ${index > 0 ? 'border-l border-slate-200 dark:border-slate-700' : ''}`}
            >
              <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${toneClasses[metric.tone ?? 'neutral']}`} />
                <span className="truncate">{metric.label}</span>
              </div>
              <div className="mt-0.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <strong className="text-base font-semibold text-slate-900 dark:text-white">
                  {loading ? '—' : metric.value}
                </strong>
                {metric.detail && (
                  <span className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {metric.detail}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default WorkspaceOverview;
